import { useCallback, useEffect, useState } from "react";
import throttle from "lodash/throttle";
import { milliseconds } from "../util/duration";
import { keys } from "../strings";

type AppendFunction<T> = (id: string, data: T) => void;
type FetchFunction<T> = (id: string) => T | undefined;
type CheckFunction = (id: string) => boolean;

/**
 * Custom hook to manage a cache of data.
 * It allows appending data to the cache and fetching it by ID.
 *
 * @template T - The type of data to be cached
 * @returns {[AppendFunction<T>, FetchFunction<T>, CheckFunction]} - A tuple containing the append and fetch functions
 */
function useCache<T>(): [AppendFunction<T>, FetchFunction<T>, CheckFunction] {
  const cacheKey = keys.localStorage.educationCache;
  const cacheExpirationKey = keys.localStorage.educationCacheExpiration;
  const [cacheMap, setCacheMap] = useState<Map<string, T>>(
    new Map<string, T>()
  );

  /**
   * Sets a new expiration date for the cache.
   * The expiration date is set to one week from the current time.
   */
  const setNewExpiration = () => {
    const expirationDate = new Date();
    expirationDate.setTime(expirationDate.getTime() + milliseconds.oneWeek);
    localStorage.setItem(
      cacheExpirationKey,
      expirationDate.getTime().toString()
    );
  };

  /**
   * Clears the cache from local storage and resets the cache map.
   */
  const clearCache = () => {
    localStorage.removeItem(cacheKey);
    localStorage.removeItem(cacheExpirationKey);
    cacheMap.clear();
  };

  /**
   * Instantiates the cache from local storage.
   * If the cache has expired or does not exist, it returns a new empty Map.
   * Otherwise, it parses the cached data and returns it as a Map.
   *
   * @returns {Map<string, T>} - The cache map
   */
  const instantiateCache = useCallback(() => {
    const cached = localStorage.getItem(cacheKey);
    const expirationDate = localStorage.getItem(cacheExpirationKey);
    const now = new Date().getTime();

    // Check if the cache has expired
    if (!expirationDate || now > parseInt(expirationDate, 10)) {
      clearCache();
      setNewExpiration();
      return new Map<string, T>();
    }

    if (cached) {
      try {
        const parsed: [string, T][] = JSON.parse(cached);
        return new Map<string, T>(parsed);
      } catch {
        return new Map<string, T>();
      }
    }
    return new Map<string, T>();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const cache = instantiateCache();
    setCacheMap(cache);
  }, [instantiateCache, setCacheMap]);

  /**
   * Throttled function to store the cache in local storage
   * It prevents excessive writes to local storage by limiting the frequency of updates
   */
  const storeInLocalStorage = throttle(() => {
    const currentCache = Array.from(cacheMap.entries());
    const stringedCache = JSON.stringify(currentCache);
    localStorage.setItem(cacheKey, stringedCache);
  }, milliseconds.fiveSeconds);

  /**
   * Appends the data to the cache map
   *
   * @param {string} id - The ID of the data
   * @param {T} data - The data to cache
   */
  const append = useCallback(
    (id: string, data: T) => {
      cacheMap.set(id, data);
      storeInLocalStorage();
    },
    [cacheMap] // eslint-disable-line react-hooks/exhaustive-deps
  );

  /**
   * Fetches the data from the cache map by ID.
   *
   * @param {string} id - The ID of the data to fetch
   * @returns {T | undefined} - The cached data or undefined if not found
   */
  const fetch = useCallback(
    (id: string) => {
      return cacheMap.get(id);
    },
    [cacheMap]
  );

  /**
   * Checks if the cache contains data for the given ID
   *
   * @param {string}
   id - The ID to check in the cache
   * @returns {boolean} - True if the cache contains data for the ID, false otherwise
   */
  const cacheExists = useCallback(
    (id: string): boolean => {
      return cacheMap.has(id);
    },
    [cacheMap]
  );

  return [append, fetch, cacheExists];
}

export default useCache;
